// Canonical code-export framework set (frames only; DS code export removed). Known
// frameworks get server-owned scaffolding instructions; clients can also send any string
// (e.g. "SwiftUI") paired with `customInstructions` to describe the scaffold. No
// sentinel — unknown string = custom. Exported as a runtime `as const` array so callers
// can iterate/include-check at runtime without redeclaring. The `(string & {})` keeps
// literal autocomplete while allowing any string on the wire.
export const EXPORT_CODE_FRAMEWORKS = [
	'React + Tailwind',
	'Next.js + Tailwind',
	'Plain HTML/CSS',
] as const;

export type ExportCodeFramework = (typeof EXPORT_CODE_FRAMEWORKS)[number];
export type ExportCodeFrameworkOrCustom = ExportCodeFramework | (string & {});

export interface ExportFramesCodeRequest {
	frameIds: string[];
	framework: ExportCodeFrameworkOrCustom;
	customInstructions?: string;
	// Desired download filename. Plumbed into the GCS signed URL via
	// response-content-disposition so cross-origin downloads keep the pretty name.
	filename?: string;
}

export interface ExportFramesCodeResponse {
	exportId: string;
}

export interface ExportFramesAssetsRequest {
	frameIds: string[];
	// Raw per-frame formats (png/html) and the grouped formats (html+images/images).
	// For raw formats with a single frame the server emits the file directly; with
	// multiple frames it bundles into a zip. Grouped formats always zip. SVG exports
	// are produced client-side (studio's proven devigner postMessage path) and
	// uploaded via /export/upload-asset — see ExportPrebuiltAssetRequest.
	format: 'png' | 'html' | 'html+images' | 'images';
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportFramesAssetsResponse {
	exportId: string;
}

// Generic upload-then-mint-ExportLink endpoint used by client-packaged exports
// (currently just SVG — studio serializes the DOM in an iframe and builds the
// final artifact locally; forge only stores it and returns an exportId).
export interface ExportPrebuiltAssetRequest {
	// Base64-encoded bytes of the asset to store.
	data: string;
	// Desired download filename. Served via the same signed-URL path as other exports.
	filename: string;
	// File extension for the stored object (e.g. 'svg' or 'zip').
	extension: string;
	// Human-readable label shown in the downloads center.
	label: string;
}

export interface ExportPrebuiltAssetResponse {
	exportId: string;
}

// Max frames per LLM-backed export request (code and AI). Enforced on both client and server.
export const MAX_LLM_EXPORT_FRAMES = 10;

export type ExportFramesAiPreset = 'spec' | 'style' | 'custom';

export interface ExportFramesAiRequest {
	frameIds: string[];
	preset: ExportFramesAiPreset;
	customBrief?: string;
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportFramesAiResponse {
	exportId: string;
}

// DS export presets. `all` = complete DS pack; `components` / `tokens` are filtered
// slices; `custom` runs an LLM agent that pulls DS content via tools based on the user's
// free-form prompt.
export type ExportDsAiPreset = 'all' | 'components' | 'tokens' | 'custom';

export interface ExportDsAiRequest {
	designSystemId: string;
	preset: ExportDsAiPreset;
	componentNames?: string[];
	tokenDomains?: string[];
	customPrompt?: string;
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportDsAiResponse {
	exportId: string;
}
